import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { errorMsg, images } from "../../../utils/customFn";
import ModalImage from "react-modal-image";
import "./chatwindow.scss";
import useApiRequest from "../../../hook/useApiRequest";
import { API_ENDPOINTS } from "../../../constants/endPoints";
import { useNavigate } from "react-router-dom";

const ChatWindow = ({
  socket,
  otherUser,
  messages,
  setMessages,
  setListData,
}) => {
  const navigate = useNavigate();
  const { loading, fetchData } = useApiRequest();
  const { profileData } = useSelector((state) => state.userAuth);

  const adminId = profileData?.id;
  const receiverId = otherUser?.id;
  const currentUserId = profileData?.id;
  const [newMessage, setNewMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const isScrolledToBottomRef = useRef(isScrolledToBottom);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

  const messagesEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const scrollHeightRef = useRef(0);
  const prevScrollHeightRef = useRef(0);

  // Socket event handlers
  useEffect(() => {
    const handleNewMessage = (data) => {
      console.log("data", data, receiverId, adminId);

      if (
        (data.senderId == adminId && data.sender_type == "Admin") ||
        (data.senderId == receiverId && data.sender_type != "Admin")
      ) {
        const newMsg = {
          message: data.message,
          sender_id: data.senderId,
          fileUrl: data.fileUrl,
          fileName: data.fileName,
          fileType: data.fileType,
          timestamp: data.timestamp,
          sender_type:data.sender_type,
           
          seen: false,
        };

        setMessages((prev) => [...prev, newMsg]);

        socket.emit("contact_support_messages_read", {
          senderId: data.senderId,
          receiverId: 1,
        });

        if (isScrolledToBottomRef.current) {
          setUnreadMessagesCount(0);
        } else {
          setUnreadMessagesCount((prev) => prev + 1);
        }
      }
    };

    socket.on("chat_support_msg", handleNewMessage);

    return () => {
      socket.off("chat_support_msg", handleNewMessage);
    };
  }, [socket, receiverId, adminId]);

  // Scroll to bottom when at bottom and messages change
  useEffect(() => {
    if (isScrolledToBottomRef.current && messagesEndRef.current) {
      scrollToBottom();
    } else if (messages.length > 0) {
      // Increment unread count only for new messages
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender_id !== currentUserId) {
        // setUnreadMessagesCount((prev) => prev + 1);
      }
    }
  }, [messages, currentUserId]);

  // Initial data fetch
  useEffect(() => {
    if (!receiverId) return;

    setMessages([]);
    setCurrentPage(1);
    setTotalPages(1);
    setUnreadMessagesCount(0);
    scrollHeightRef.current = 0;
    prevScrollHeightRef.current = 0;

    if (receiverId) {
      setTimeout(() => {
        fetchChatHistory(1);
        socket.emit("contact_support_messages_read", {
          senderId: receiverId,
          receiverId: profileData?.id,
        });
      }, 0);
    }
  }, [otherUser]);

  // Scroll handling
  useEffect(() => {
    const handleScroll = () => {
      if (!chatMessagesRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = chatMessagesRef.current;
      const atBottom = scrollHeight - scrollTop <= clientHeight + 10;

      setIsScrolledToBottom(atBottom);
      isScrolledToBottomRef.current = atBottom;

      // Reset unread count when scrolling to bottom
      if (atBottom) setUnreadMessagesCount(0);

      // Load more when scrolled to top
      if (scrollTop < 50 && !isLoadingMore && currentPage < totalPages) {
        loadMoreMessages();
      }
    };

    const chatMessages = chatMessagesRef.current;
    if (chatMessages) {
      chatMessages.addEventListener("scroll", handleScroll);
      return () => chatMessages.removeEventListener("scroll", handleScroll);
    }
  }, [isLoadingMore, currentPage, totalPages, otherUser]);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      setIsScrolledToBottom(true);
      isScrolledToBottomRef.current = true;
    }
  }, []);

  const fetchChatHistory = async (page = 1) => {
    const url = API_ENDPOINTS.chat_support_chat_history;
    const params = {
      userId: receiverId,
      adminId: profileData?.id,
      page,
      pageSize: 20,
    };

    const response = await fetchData(
      `${url}?${new URLSearchParams(params)}`,
      navigate
    );

    if (response) {
      setMessages((prev) => [...response.messages, ...prev]);
      setTotalPages(response.pagination.totalPages);
      setCurrentPage(page);
    }
  };

  const loadMoreMessages = useCallback(async () => {
    if (isLoadingMore || currentPage >= totalPages) return;

    setIsLoadingMore(true);

    // Save current scroll position
    prevScrollHeightRef.current = chatMessagesRef.current.scrollHeight;

    await fetchChatHistory(currentPage + 1);

    // Restore scroll position after loading
    setTimeout(() => {
      if (chatMessagesRef.current) {
        const newScrollHeight = chatMessagesRef.current.scrollHeight;
        const scrollOffset = newScrollHeight - prevScrollHeightRef.current;
        chatMessagesRef.current.scrollTop = scrollOffset;
      }
      setIsLoadingMore(false);
    }, 0);
  }, [
    currentPage,
    totalPages,
    isLoadingMore,
    fetchChatHistory,
    receiverId,
    adminId,
  ]);

  const sendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;

    try {
      let fileData = null;
      if (selectedFile) {
        const reader = new FileReader();
        reader.readAsDataURL(selectedFile);
        await new Promise((resolve) => (reader.onload = resolve));
        fileData = {
          data: reader.result.split(",")[1],
          name: selectedFile.name,
          type: selectedFile.type,
        };
      }

      socket.emit("contact_support", {
        senderId: currentUserId,
        receiverId,
        message: newMessage,
        senderType: "Admin",
        receiverType: otherUser?.customer_type,
        file: fileData,
      });

      setListData((prevList) => {
        const userIndex = prevList.findIndex((user) => user.id == receiverId);

        if (userIndex == -1) {
          return prevList;
        }

        const updatedList = [...prevList];
        const [movedUser] = updatedList.splice(userIndex, 1);

        const updatedUser = {
          ...movedUser,
          latestMessage: {
            message: newMessage || "",
          },
        };

        return [updatedUser, ...updatedList];
      });

      setNewMessage("");
      setSelectedFile(null);
      setFilePreview(null);

      socket.emit("contact_support_messages_read", {
        senderId: currentUserId,
        receiverId: profileData?.id,
      });

      // Scroll to bottom after sending
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      errorMsg("Failed to send message");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      const type = file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("video/")
        ? "video"
        : file.type.startsWith("audio/")
        ? "audio"
        : "document";

      setFilePreview({
        type,
        url: reader.result,
        name: file.name,
      });
    };
    reader.readAsDataURL(file);
  };

  const deleteFilePreview = () => {
    setSelectedFile(null);
    setFilePreview(null);
  };

  const isNewDay = (prevTimestamp, currTimestamp) => {
    const prevDate = new Date(prevTimestamp).toDateString();
    const currDate = new Date(currTimestamp).toDateString();
    return prevDate !== currDate;
  };

  return (
    <div className="chat-window">
      {otherUser ? (
        <>
          <header className="chat-header">
            <div className="chat-header-left justify-content-between">
              <div className="d-flex align-items-center">
                <img
                  src={otherUser?.profile_picture || images["dummy.png"]}
                  alt={`${otherUser?.name}'s avatar`}
                  className="avatar"
                />
                <h5>{otherUser?.name}</h5>
              </div>
              <p>
                <button className="mobile-menu-toggle">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 30 30"
                    width="24px"
                    height="24px"
                  >
                    <path d="M 3 7 A 1.0001 1.0001 0 1 0 3 9 L 27 9 A 1.0001 1.0001 0 1 0 27 7 L 3 7 z M 3 14 A 1.0001 1.0001 0 1 0 3 16 L 27 16 A 1.0001 1.0001 0 1 0 27 14 L 3 14 z M 3 21 A 1.0001 1.0001 0 1 0 3 23 L 27 23 A 1.0001 1.0001 0 1 0 27 21 L 3 21 z"></path>
                  </svg>
                </button>
              </p>
            </div>
          </header>

          <div className="chat-messages" ref={chatMessagesRef}>
            {isLoadingMore && (
              <div className="loading-indicator">Loading older messages...</div>
            )}

            {messages.map((msg, index) => (
              <React.Fragment key={index}>
                {index === 0 ||
                isNewDay(messages[index - 1].timestamp, msg.timestamp) ? (
                  <>
                    {new Date(msg.timestamp).toLocaleDateString() !==
                      "Invalid Date" && (
                      <div className="date-divider">
                        <span>
                          {new Date(msg.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </>
                ) : null}

                {msg.message && (
                  <div
                    className={`message ${
                      msg.sender_type == "Admin"  ? "sent" : "received"
                    }`}
                  >

                     {msg.sender_type !== "Admin"  && (
                        <img
                          src={otherUser?.profile_picture}
                          alt="User Avatar"
                          className="avatar"
                        />
                      )}

                    <div className="message_container">
                      <p
                        className={`timestamp ${
                          msg.sender_type === "Admin" 
                            ? "text-end"
                            : "text-left"
                        }`}
                      >
                        {/* <span className="username_in_chat">
                          {msg.sender_type == "Admin"
                            ? profileData?.name
                            : otherUser?.name}
                        </span> */}
                        {msg.timestamp}
                      </p>
                      <div className="message-content">
                        <p>{msg.message}</p>
                      </div>
                    </div>

                    {msg.sender_type === "Admin"  && (
                    <img
                      src={profileData?.image}
                      alt="User Avatar"
                      className="avatar"
                    />
                  )}

                  </div>
                )}

                {msg?.fileUrl && (
                  <div
                    className={`chat-file ${
                      msg.sender_id === currentUserId ? "ml-auto" : ""
                    }`}
                  >
                    <div className="file-preview">
                      {msg.fileType === "image" ? (
                        <ModalImage
                          small={msg.fileUrl}
                          large={msg.fileUrl}
                          alt={msg.fileName}
                        />
                      ) : msg.fileType === "video" ? (
                        <video controls width="200" height="120">
                          <source src={msg.fileUrl} type="video/mp4" />
                          Your browser does not support videos.
                        </video>
                      ) : (
                        <div className="file-attachment">
                          <div className="file-icon">
                            {msg.fileType === "application/pdf" ? (
                              <img src={images["pdf.png"]} alt="PDF" />
                            ) : msg.fileType.includes("audio") ? (
                              <img src={images["audio.png"]} alt="Audio" />
                            ) : (
                              <img src={images["unknown.png"]} alt="File" />
                            )}
                          </div>
                          <div className="file-info">
                            <div className="file-name">{msg.fileName}</div>
                          </div>
                          <a
                            href={msg.fileUrl}
                            download
                            className="file-download"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="24"
                              viewBox="0 0 24 24"
                              width="24"
                            >
                              <path d="M0 0h24v24H0z" fill="none" />
                              <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                            </svg>
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="input-box d-flex flex-column sender_file_preview">
            {filePreview && (
              <div className="file-preview-container mb-3">
                {filePreview.type === "image" ? (
                  <div className="position-relative d-inline-block">
                    <img
                      src={filePreview.url}
                      alt="Preview"
                      width={100}
                      className="img-thumbnail"
                    />
                  </div>
                ) : filePreview.type === "video" ? (
                  <div className="position-relative d-inline-block">
                    <video
                      src={filePreview.url}
                      width={100}
                      className="img-thumbnail"
                    />
                  </div>
                ) : (
                  <div className="file-attachment position-relative">
                    <div className="file-icon">
                      {filePreview.type === "application/pdf" ? (
                        <img src={images["pdf.png"]} alt="PDF" width={24} />
                      ) : filePreview.type.includes("audio") ? (
                        <img src={images["audio.png"]} alt="Audio" width={24} />
                      ) : (
                        <img
                          src={images["unknown.png"]}
                          alt="File"
                          width={24}
                        />
                      )}
                    </div>
                    <div className="file-info">
                      <div className="file-name small">{filePreview.name}</div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="message-input">
              <input
                type="text"
                placeholder="Type message"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key == "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
              />

              <input
                type="file"
                onChange={handleFileChange}
                style={{ display: "none" }}
                id="fileInput"
              />
              {/* <label htmlFor="fileInput" className="upload-file-icon">
            <img src={images["attach-symbol.png"]} alt="Attach file" />
          </label> */}

              <div className="send-chat-icon" onClick={sendMessage}>
                <img src={images["send.png"]} alt="Send" />
              </div>
            </div>
          </div>

          {!isScrolledToBottom && (
            <div className="scroll-arrow" onClick={scrollToBottom}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="#4633AF"
                width="36px"
                height="36px"
              >
                <path d="M12 12c-.26 0-.52-.1-.71-.29l-5-5a1.003 1.003 0 0 1 1.42-1.42L12 9.59l4.29-4.3a1.003 1.003 0 0 1 1.42 1.42l-5 5c-.19.18-.45.29-.71.29z" />
                <path d="M12 20c-.26 0-.52-.1-.71-.29l-5-5a1.003 1.003 0 0 1 1.42-1.42L12 17.59l4.29-4.3a1.003 1.003 0 0 1 1.42 1.42l-5 5c-.19.18-.45.29-.71.29z" />
              </svg>
              {unreadMessagesCount > 0 && (
                <span className="unread-count">{unreadMessagesCount}</span>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="no-chat-selected">
          <div className="placeholder-content">
            <div className="chat-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="80"
                height="80"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#a0aec0"
                strokeWidth="1.5"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
            </div>
            <h3>Select a conversation</h3>
            <p>Choose a contact from the list to start messaging</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
